import React, { useState, useRef, ChangeEvent, KeyboardEvent, ElementType } from 'react';
import { styled, Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const EditableContent = styled('textarea')<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
  width: '100%',
  minHeight: '200px',
  height: '300px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  border: `1px solid ${isEmpty ? theme.palette.error.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: '0.8rem',
  lineHeight: '1.5',
  outline: 'none',
  '&:focus': {
    borderColor: isEmpty ? theme.palette.error.main : theme.palette.primary.main,
    boxShadow: isEmpty
      ? `0 0 0 2px ${theme.palette.error.main}25`
      : `0 0 0 2px ${theme.palette.primary.main}25`,
  },
}));

const MarkdownPreview = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '200px',
  height: '300px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  cursor: 'text',
  fontFamily: 'inherit',
  fontSize: '0.8rem',
  '&:hover': {
    borderColor: theme.palette.action.hover,
  },
  '& p': {
    margin: '0 0 16px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '16px 0 8px 0',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  '& ul, & ol': {
    margin: '0 0 16px 0',
    paddingLeft: '24px',
  },
  '& li': {
    margin: '4px 0',
  },
  '& code': {
    backgroundColor: theme.palette.action.hover,
    padding: '2px 4px',
    borderRadius: '4px',
    fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '0.875em',
  },
  '& pre': {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    margin: '16px 0',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),

}));

interface MarkdownEditorProps {
  id?: string;
  value?: string;
  onChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export const InstructionContent: ElementType<MarkdownEditorProps> = (
  { value, onChange, onKeyDown }) => {

  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handlePreviewClick = () => {
    setIsFocused(true);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value: textValue } = textarea;

    // Handle Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();

      if (e.shiftKey) {
        // Shift+Tab: Outdent
        const lines = textValue.split('\n');
        const startLine = textValue.substring(0, selectionStart).split('\n').length - 1;
        const endLine = textValue.substring(0, selectionEnd).split('\n').length - 1;

        let newText = '';
        let charsRemoved = 0;
        let newSelectionStart = selectionStart;
        let newSelectionEnd = selectionEnd;

        lines.forEach((line, i) => {
          if (i >= startLine && i <= endLine) {
            // Remove up to 2 spaces or 1 tab from the start of the line
            if (line.startsWith('  ')) {
              const removedLine = line.substring(2);
              newText += (i > 0 ? '\n' : '') + removedLine;
              if (i === startLine) newSelectionStart -= 2;
              if (i <= endLine) newSelectionEnd -= 2;
            } else if (line.startsWith('\t')) {
              const removedLine = line.substring(1);
              newText += (i > 0 ? '\n' : '') + removedLine;
              if (i === startLine) newSelectionStart -= 1;
              if (i <= endLine) newSelectionEnd -= 1;
            } else if (line.startsWith(' ')) {
              const removedLine = line.substring(1);
              newText += (i > 0 ? '\n' : '') + removedLine;
              if (i === startLine) newSelectionStart -= 1;
              if (i <= endLine) newSelectionEnd -= 1;
            } else {
              newText += (i > 0 ? '\n' : '') + line;
            }
          } else {
            newText += (i > 0 ? '\n' : '') + line;
          }
        });

        onChange(newText);
        setTimeout(() => {
          textarea.selectionStart = Math.max(0, newSelectionStart);
          textarea.selectionEnd = Math.max(0, newSelectionEnd);
        }, 0);
      } else {
        // Tab: Indent
        const indent = '  '; // 2 spaces

        if (selectionStart === selectionEnd) {
          // No selection: insert indent at cursor
          const newText = textValue.substring(0, selectionStart) + indent + textValue.substring(selectionStart);
          onChange(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart + indent.length;
          }, 0);
        } else {
          // Selection: indent all selected lines
          const lines = textValue.split('\n');
          const startLine = textValue.substring(0, selectionStart).split('\n').length - 1;
          const endLine = textValue.substring(0, selectionEnd).split('\n').length - 1;

          const newLines = lines.map((line, i) => {
            if (i >= startLine && i <= endLine) {
              return indent + line;
            }
            return line;
          });

          const newText = newLines.join('\n');
          onChange(newText);
          
          setTimeout(() => {
            textarea.selectionStart = selectionStart + indent.length;
            textarea.selectionEnd = selectionEnd + (indent.length * (endLine - startLine + 1));
          }, 0);
        }
      }
    }
    // Handle Enter key for list continuation
    else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      const lines = textValue.substring(0, selectionStart).split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check for unordered list (-, *, +)
      const unorderedMatch = currentLine.match(/^(\s*)([-*+])\s+(.*)$/);
      if (unorderedMatch) {
        e.preventDefault();
        const [, indent, bullet, content] = unorderedMatch;
        
        // If the line only has the bullet (no content), remove the bullet
        if (!content.trim()) {
          const newText = textValue.substring(0, selectionStart - currentLine.length) + 
                         indent + '\n' + textValue.substring(selectionStart);
          onChange(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart - currentLine.length + indent.length + 1;
          }, 0);
        } else {
          // Continue the list
          const newText = textValue.substring(0, selectionStart) + 
                         '\n' + indent + bullet + ' ' + textValue.substring(selectionEnd);
          onChange(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart + indent.length + bullet.length + 2;
          }, 0);
        }
        return;
      }
      
      // Check for ordered list (1., 2., etc.)
      const orderedMatch = currentLine.match(/^(\s*)(\d+)\.\s+(.*)$/);
      if (orderedMatch) {
        e.preventDefault();
        const [, indent, number, content] = orderedMatch;
        
        // If the line only has the number (no content), remove the number
        if (!content.trim()) {
          const newText = textValue.substring(0, selectionStart - currentLine.length) + 
                         indent + '\n' + textValue.substring(selectionStart);
          onChange(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart - currentLine.length + indent.length + 1;
          }, 0);
        } else {
          // Continue the list with incremented number
          const nextNumber = parseInt(number) + 1;
          const newText = textValue.substring(0, selectionStart) + 
                         '\n' + indent + nextNumber + '. ' + textValue.substring(selectionEnd);
          onChange(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart + indent.length + nextNumber.toString().length + 3;
          }, 0);
        }
        return;
      }
    }

    // Call the parent's onKeyDown handler
    onKeyDown(e);
  };

  return (
    <Box 
        display="flex" 
        flexDirection="column" 
        sx={{
            width: '100%',
            overflowX: 'hidden',
            flexShrink: 0
        }}
    >
      {(isFocused || !value?.trim()) ? (
        <>
          <EditableContent
            ref={textareaRef}
            isEmpty={!value?.trim()}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <HelperText>
            Use Markdown formatting. Tab/Shift+Tab to indent/outdent. Ctrl+Enter to save.
          </HelperText>
        </>
      ) : (
        <MarkdownPreview onClick={handlePreviewClick}>
          <ReactMarkdown>{value}</ReactMarkdown>
        </MarkdownPreview>
      )}
    </Box>
  );
}